class PackSelector {
  constructor(element) {
    this.element = element;
    this.productId = element.dataset.productId;
    this.configData = this.loadConfig();
    
    if (!this.configData) {
      console.error('Pack config data not found');
      return;
    }
    
    this.init();
  }
  
  loadConfig() {
    const configElement = document.querySelector(`.pack-config-data[data-product-id="${this.productId}"]`);
    if (!configElement) return null;
    
    try {
      return JSON.parse(configElement.textContent);
    } catch (e) {
      console.error('Error parsing pack config:', e);
      return null;
    }
  }
  
  init() {
    this.packOptions = this.element.querySelectorAll('input[name^="pack-type"]');
    this.qtyInput = this.element.querySelector('.qty-input');
    this.qtyMinus = this.element.querySelector('.qty-minus');
    this.qtyPlus = this.element.querySelector('.qty-plus');
    this.addToCartBtn = this.element.querySelector('.pack-add-to-cart');
    this.totalPacksDisplay = this.element.querySelector('.total-packs');
    this.totalPriceDisplay = this.element.querySelector('.total-price');
    this.unitPriceDisplay = this.element.querySelector('.unit-price-value');
    this.messageBox = this.element.querySelector('.pack-selector-message');
    
    this.bindEvents();
    this.updateDisplay();
  }
  
  bindEvents() {
    this.packOptions.forEach(option => {
      option.addEventListener('change', () => this.updateDisplay());
    });
    
    this.qtyInput.addEventListener('input', () => this.updateDisplay());
    this.qtyMinus.addEventListener('click', () => this.decreaseQty());
    this.qtyPlus.addEventListener('click', () => this.increaseQty());
    this.addToCartBtn.addEventListener('click', () => this.addToCart());
  }
  
  getSelectedOption() {
    const selected = Array.from(this.packOptions).find(opt => opt.checked);
    if (!selected) return null;
    
    return {
      type: selected.value,
      multiplier: parseInt(selected.dataset.multiplier),
      price: parseFloat(selected.dataset.price),
      label: selected.dataset.label
    };
  }
  
  getQuantity() {
    return parseInt(this.qtyInput.value) || 1;
  }
  
  decreaseQty() {
    const current = this.getQuantity();
    if (current > 1) {
      this.qtyInput.value = current - 1;
      this.updateDisplay();
    }
  }
  
  increaseQty() {
    const current = this.getQuantity();
    this.qtyInput.value = current + 1;
    this.updateDisplay();
  }
  
  updateDisplay() {
    const option = this.getSelectedOption();
    const qty = this.getQuantity();
    
    if (!option) return;
    
    // Calculate totals
    const totalPacks = qty * option.multiplier;
    const totalUnits = totalPacks * this.configData.units_per_pack;
    const totalPrice = qty * option.price;
    const pricePerUnit = totalPrice / totalUnits;
    
    // Format numbers
    const formattedUnits = this.formatNumber(totalUnits);
    const formattedPrice = this.formatMoney(totalPrice);
    const formattedUnitPrice = this.formatMoney(pricePerUnit);
    
    // Update displays
    if (option.type === 'einzelpack') {
      this.totalPacksDisplay.textContent = `${qty} ${qty === 1 ? 'Pack' : 'Packs'} (${formattedUnits} ${this.configData.unit_label})`;
    } else if (option.type === 'karton') {
      this.totalPacksDisplay.textContent = `${qty} ${qty === 1 ? 'Karton' : 'Kartons'} (${totalPacks} Packs = ${formattedUnits} ${this.configData.unit_label})`;
    } else if (option.type === 'palette') {
      this.totalPacksDisplay.textContent = `${qty} ${qty === 1 ? 'Palette' : 'Paletten'} (${totalPacks} Packs = ${formattedUnits} ${this.configData.unit_label})`;
    }
    
    this.totalPriceDisplay.textContent = formattedPrice;
    this.unitPriceDisplay.textContent = `${formattedUnitPrice} / ${this.configData.unit_label}`;
  }
  
  formatNumber(num) {
    if (num >= 1000) {
      const thousands = Math.floor(num / 1000);
      const remainder = num % 1000;
      if (remainder === 0) {
        return `${thousands}.000`;
      }
      return `${thousands}.${remainder.toString().padStart(3, '0')}`;
    }
    return num.toString();
  }
  
  formatMoney(amount) {
    return `${this.configData.currency}${amount.toFixed(2).replace('.', ',')}`;
  }
  
  async addToCart() {
    const option = this.getSelectedOption();
    const qty = this.getQuantity();
    const variantId = this.addToCartBtn.dataset.variantId;
    
    if (!option || !variantId) {
      this.showMessage('Fehler beim Hinzufügen zum Warenkorb', 'error');
      return;
    }
    
    // Calculate totals for properties
    const totalPacks = qty * option.multiplier;
    const totalUnits = totalPacks * this.configData.units_per_pack;
    const totalPrice = qty * option.price;
    const pricePerUnit = totalPrice / totalUnits;
    
    // Prepare line item properties
    let displayLabel = '';
    if (option.type === 'einzelpack') {
      displayLabel = `${qty} ${qty === 1 ? 'Pack' : 'Packs'} (${this.formatNumber(totalUnits)} ${this.configData.unit_label})`;
    } else if (option.type === 'karton') {
      displayLabel = `${qty} ${qty === 1 ? 'Karton' : 'Kartons'} (${totalPacks} Packs × ${this.configData.units_per_pack} ${this.configData.unit_label} = ${this.formatNumber(totalUnits)} ${this.configData.unit_label})`;
    } else if (option.type === 'palette') {
      displayLabel = `${qty} ${qty === 1 ? 'Palette' : 'Paletten'} (${totalPacks} Packs × ${this.configData.units_per_pack} ${this.configData.unit_label} = ${this.formatNumber(totalUnits)} ${this.configData.unit_label})`;
    }
    
    const properties = {
      '_pack_type': option.label,
      '_customer_quantity': qty.toString(),
      '_display_label': displayLabel,
      '_total_packs': totalPacks.toString(),
      '_total_units': `${this.formatNumber(totalUnits)} ${this.configData.unit_label}`,
      '_unit_price': `${this.formatMoney(pricePerUnit)} / ${this.configData.unit_label}`
    };
    
    // Disable button
    this.addToCartBtn.disabled = true;
    this.addToCartBtn.textContent = 'Wird hinzugefügt...';
    
    try {
      const formData = {
        items: [{
          id: variantId,
          quantity: totalPacks, // This is the actual inventory deduction
          properties: properties,
          price: Math.round(option.price * 100) // Price in cents
        }]
      };
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }
      
      const data = await response.json();
      
      // Show success message
      this.showMessage('Erfolgreich zum Warenkorb hinzugefügt!', 'success');
      
      // Trigger cart update event
      document.dispatchEvent(new CustomEvent('cart:updated'));
      
      // Update cart count if element exists
      const cartCount = document.querySelector('.cart-count, #cart-icon-bubble');
      if (cartCount) {
        const currentCount = await this.getCartCount();
        cartCount.textContent = currentCount;
      }
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showMessage('Fehler beim Hinzufügen zum Warenkorb. Bitte versuchen Sie es erneut.', 'error');
    } finally {
      // Re-enable button
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2 5m4-5h10m0 0l2 5m-2-5v5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        In den Warenkorb
      `;
    }
  }
  
  async getCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return cart.item_count;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return 0;
    }
  }
  
  showMessage(text, type) {
    this.messageBox.textContent = text;
    this.messageBox.className = `pack-selector-message ${type}`;
    this.messageBox.style.display = 'block';
    
    setTimeout(() => {
      this.messageBox.style.display = 'none';
    }, 5000);
  }
}

// Initialize all pack selectors on page
document.addEventListener('DOMContentLoaded', () => {
  const selectors = document.querySelectorAll('.custom-pack-selector');
  selectors.forEach(selector => new PackSelector(selector));
});
